import React from "react";
import { AgGrid, Flex, Tag } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import type { IconType } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { WORKFLOW_STATUS_LABELS } from "../constants";
import { getTaskStatusDisplay, getGpxStatusDisplay } from "../taskStatusConfig";
import * as styles from "./styles_vanilla.css";
import type { WorkflowStatus, WorkflowTableRow } from "../types";

export type WorkflowGridContext = {
  onTaskClick?: (workflowId: string, taskId: string) => void;
};

// AG Grid only generics `<TData, TValue>`. Use Omit to strip the upstream
// `context: any` then re-add a typed one so consumers (and ESLint) see a typed
// handler rather than `any`.
type WfCellParams = Omit<AgGrid.ICellRendererParams<WorkflowTableRow>, "context"> & {
  context?: WorkflowGridContext;
};

function isChildDisabled(data: WorkflowTableRow): boolean {
  if (data.isParentRow) return false;
  return (
    data.status === "BLOCKED" ||
    data.status === "NOT_STARTED" ||
    data.status === "COMPLETED" ||
    data.status === "SKIPPED"
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const formatDays = (n: number) => `${n} day${n === 1 ? "" : "s"}`;

function getSlaText(
  dueDate: string | null | undefined,
): { text: string; color: string; className: string } | null {
  if (!dueDate) return null;
  const now = new Date();
  const due = new Date(dueDate);
  const diffMs = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0)
    return {
      text: `Overdue by ${formatDays(Math.abs(diffDays))}`,
      color: Colors.Danger600,
      className: styles.slaTextDanger,
    };
  if (diffDays === 0)
    return {
      text: "Due today",
      color: Colors.Danger600,
      className: styles.slaTextDanger,
    };
  if (diffDays <= 3)
    return {
      text: `Due in ${formatDays(diffDays)}`,
      color: Colors.Danger600,
      className: styles.slaTextDanger,
    };
  if (diffDays <= 7)
    return {
      text: `Due in ${formatDays(diffDays)}`,
      color: Colors.Warning600,
      className: styles.slaTextWarning,
    };
  return {
    text: `Due in ${formatDays(diffDays)}`,
    color: Colors.Neutral500,
    className: styles.slaTextNeutral,
  };
}

const DoughnutProgress: React.FC<{ completed: number; total: number }> = ({
  completed,
  total,
}) => {
  const pct = total > 0 ? completed / total : 0;
  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - pct);

  return (
    <svg width={16} height={16} viewBox="0 0 16 16" className={styles.doughnutSvg}>
      <circle
        cx={8}
        cy={8}
        r={radius}
        fill="none"
        stroke={Colors.Neutral300}
        strokeWidth={2.5}
      />
      {pct > 0 && (
        <circle
          cx={8}
          cy={8}
          r={radius}
          fill="none"
          stroke={Colors.Primary600}
          strokeWidth={2.5}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform="rotate(-90 8 8)"
        />
      )}
    </svg>
  );
};

/**
 * Scope/version tag shown next to the ticket id: which definition built this
 * workflow. ARENA → "v{n}"; GLOBAL → "Global \u00B7 v{n}". Styled (via styles.versionTag)
 * to read like the ticket id — same Moderat font and grey, just compact.
 * Null scope (Jira-sourced or unresolved) renders nothing.
 */
const renderDefinitionTag = (data: WorkflowTableRow): React.ReactNode => {
  const { definitionScope, definitionVersionNumber } = data;
  // Scope always carries a resolved version from the BE; guard keeps the type
  // narrowed to a number and avoids rendering a versionless tag.
  if (definitionVersionNumber == null) {
    return null;
  }
  if (definitionScope === "ARENA") {
    return (
      <Tag
        variant="neutral"
        className={styles.versionTag}
        label={`v${definitionVersionNumber}`}
      />
    );
  }
  if (definitionScope === "GLOBAL") {
    return (
      <Tag
        variant="neutral"
        className={styles.versionTag}
        label={`Global \u00B7 v${definitionVersionNumber}`}
      />
    );
  }
  return null;
};

/**
 * ID cell for the Canopy "My workflows" view, where the Description cell runs
 * its single-line branch and so never reaches renderDefinitionTag. Puts the
 * version tag beside the external ref instead. Parent rows only, matching the
 * column's valueGetter; gated on real content so an unresolved definition never
 * leaves an empty chip behind.
 */
export const WorkflowIdCellRenderer: React.FC<WfCellParams> = ({ data }) => {
  if (!data?.isParentRow) return null;

  const definitionTag = renderDefinitionTag(data);
  if (!data.externalRefId && !definitionTag) return null;

  return (
    <div className={styles.idCellRow}>
      {data.externalRefId && <span className={styles.idCellRef}>{data.externalRefId}</span>}
      {definitionTag && <span className={styles.idCellTag}>{definitionTag}</span>}
    </div>
  );
};

export const DescriptionInnerRenderer: React.FC<WfCellParams> = ({ data, context }) => {
  if (!data) return null;

  const handleTaskClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    const onTaskClick = context?.onTaskClick;
    if (onTaskClick && data.workflowId && data.currentStepTaskId) {
      onTaskClick(data.workflowId, data.currentStepTaskId);
    }
  };

  if (data.isParentRow) {
    if (data.showIdSubtitle) {
      // Gate the subtitle row on real content (id or a rendered tag) so a
      // scope with no resolvable version never leaves an empty row behind.
      const definitionTag = renderDefinitionTag(data);
      return (
        <div className={styles.cellColumnPadded}>
          <div className={styles.cellRow}>
            <span className={styles.primaryText}>{data.displayName}</span>
            {data.progress && (
              <div className={styles.progressGroup}>
                <DoughnutProgress
                  completed={data.progress.completed}
                  total={data.progress.total}
                />
                <span className={styles.progressCountText}>
                  {data.progress.completed}/{data.progress.total}
                </span>
              </div>
            )}
          </div>
          {(data.externalRefId || definitionTag) && (
            <Flex alignItems="center" gap={Sizes.XS}>
              {data.externalRefId && (
                <span className={styles.descTicketId}>{data.externalRefId}</span>
              )}
              {definitionTag}
            </Flex>
          )}
        </div>
      );
    }

    return (
      <div className={styles.cellRow}>
        <span className={styles.primaryText}>{data.displayName}</span>
        {data.progress && (
          <div className={styles.progressGroup}>
            <DoughnutProgress completed={data.progress.completed} total={data.progress.total} />
            <span className={styles.progressCountText}>
              {data.progress.completed}/{data.progress.total}
            </span>
          </div>
        )}
        {data.currentStepName && (
          <button
            type="button"
            className={styles.stepGroup}
            onClick={handleTaskClick}
            data-step-group
          >
            <Icon type="chevron-right" color={Colors.Neutral600} size="small" />
            <span className={styles.stepText}>{data.currentStepName}</span>
          </button>
        )}
      </div>
    );
  }

  if (data.isJiraPlaceholder) {
    return null;
  }

  if (data.isJiraLoading) {
    return (
      <div className={styles.cellRow}>
        <span className={styles.disabledText}>{data.displayName}</span>
      </div>
    );
  }

  return (
    <div className={styles.cellRow}>
      <span className={isChildDisabled(data) ? styles.disabledText : styles.childRowText}>
        {data.displayName}
      </span>
    </div>
  );
};

export const ClientCellRenderer: React.FC<WfCellParams> = ({ data }) => {
  if (!data?.clientName) return null;

  const clientIdNum = data.clientId?.replace("client-", "") ?? "";

  return (
    <div className={styles.cellColumn}>
      <span className={styles.primaryTextNoEllipsis}>{data.clientName}</span>
      <span className={styles.subtitleText}>{clientIdNum}</span>
    </div>
  );
};

export const SubjectCellRenderer: React.FC<WfCellParams> = ({ data }) => {
  if (!data?.entityName) return null;

  return (
    <div className={styles.cellColumn}>
      <span className={styles.primaryTextNoEllipsis}>{data.entityName}</span>
      <span className={styles.subtitleText}>Entity</span>
    </div>
  );
};

export const AssigneeCellRenderer: React.FC<WfCellParams> = ({ data }) => {
  if (!data) return null;

  const disabled = isChildDisabled(data);
  const assignee = data.assignee;
  if (!assignee) {
    // "Not assigned" renders identically across all row statuses — the
    // placeholder shouldn't visually shift between IN_PROGRESS / BLOCKED /
    // COMPLETED rows.
    return (
      <div className={styles.cellRow}>
        <Icon type="user" color={Colors.Neutral400} size="small" />
        <span className={styles.secondaryText}>Not assigned</span>
      </div>
    );
  }

  return (
    <div className={styles.cellRow}>
      <Icon type="user" color={disabled ? Colors.Neutral500 : Colors.Primary600} size="small" />
      <span className={disabled ? styles.disabledTextNoPad : styles.primaryTextNoEllipsis}>
        {assignee.displayName}
      </span>
    </div>
  );
};

export const DueDateCellRenderer: React.FC<WfCellParams> = ({ data }) => {
  if (!data) return null;

  const disabled = isChildDisabled(data);
  const dateStr = formatDate(data.dueDate);
  const sla = getSlaText(data.dueDate);

  if (!dateStr) {
    // "No due date" renders identically across all row statuses — same
    // rationale as the "Not assigned" placeholder above.
    return (
      <div className={styles.cellRow}>
        <span className={styles.secondaryText}>No due date</span>
      </div>
    );
  }

  return (
    <div className={styles.cellColumn}>
      <span className={disabled ? styles.disabledTextNoPad : styles.primaryTextNoEllipsis}>
        {dateStr}
      </span>
      {sla && (
        <span
          className={`${styles.subtitleText} ${disabled ? styles.slaTextDisabled : sla.className}`}
        >
          {sla.text}
        </span>
      )}
    </div>
  );
};

type StatusConfig = {
  label: string;
  pillClassName: string;
  iconColor: Colors;
  icon: IconType;
};

const FALLBACK_STATUS_CONFIG: StatusConfig = {
  label: "",
  pillClassName: styles.statusPillNeutral,
  iconColor: Colors.Neutral500,
  icon: "clock",
};

function getWorkflowStatusConfig(status: string): StatusConfig {
  const label = WORKFLOW_STATUS_LABELS[status as WorkflowStatus] ?? status;
  switch (status) {
    case "CLOSED":
      return {
        label,
        pillClassName: styles.statusPillSuccess,
        iconColor: Colors.Success600,
        icon: "check",
      };
    case "IN_PROGRESS":
      return {
        label,
        pillClassName: styles.statusPillPrimary,
        iconColor: Colors.Primary600,
        icon: "sync-alt",
      };
    case "PENDING_REVIEW":
      return {
        label,
        pillClassName: styles.statusPillPrimary,
        iconColor: Colors.Primary600,
        icon: "hourglass-start",
      };
    case "PENDING_EXTERNAL_APPROVAL":
      return {
        label,
        pillClassName: styles.statusPillWarningFlat,
        iconColor: Colors.Warning600,
        icon: "clock",
      };
    case "BLOCKED":
      return {
        label,
        pillClassName: styles.statusPillNeutral,
        iconColor: Colors.Neutral400,
        icon: "lock",
      };
    case "CANCELLED":
      return {
        label,
        pillClassName: styles.statusPillDanger,
        iconColor: Colors.Danger600,
        icon: "times-circle",
      };
    case "RETURNED":
      return {
        label,
        pillClassName: styles.statusPillDanger,
        iconColor: Colors.Danger600,
        icon: "arrow-left",
      };
    case "OPEN":
      return {
        label,
        pillClassName: styles.statusPillNeutral,
        iconColor: Colors.Neutral600,
        icon: "clock",
      };
    default:
      return { ...FALLBACK_STATUS_CONFIG, label };
  }
}

// Pill background is WorkflowTable-specific (the drawer's TaskList renders
// the same icon/label/color flat). Icon, label, and text color are sourced
// from the shared getTaskStatusDisplay helper to keep the two surfaces in
// lockstep.
function getTaskPillClassName(status: string): string {
  switch (status) {
    case "COMPLETED":
      return styles.statusPillSuccess;
    case "IN_PROGRESS":
    case "PENDING_REVIEW":
      return styles.statusPillPrimary;
    case "EXTERNAL_APPROVAL":
      return styles.statusPillWarningFlat;
    case "SKIPPED":
      return styles.statusPillNeutralMuted;
    case "RETURNED":
      return styles.statusPillDanger;
    case "NOT_STARTED":
    case "BLOCKED":
    default:
      return styles.statusPillNeutral;
  }
}

function getTaskStatusConfig(status: string): StatusConfig {
  const display = getTaskStatusDisplay(status);
  return {
    label: display.label,
    pillClassName: getTaskPillClassName(status),
    iconColor: display.iconColor,
    icon: display.icon,
  };
}

export const StatusCellRenderer: React.FC<WfCellParams> = ({ data }) => {
  if (!data?.status) return null;
  if (data.isParentRow && data.showIdSubtitle) return null;

  const config = data.isParentRow
    ? getWorkflowStatusConfig(data.status)
    : getTaskStatusConfig(data.status);

  return (
    <div className={styles.cellRow}>
      <span className={`${styles.statusPillBase} ${config.pillClassName}`}>
        <Icon type={config.icon} color={config.iconColor} size="small" />
        {config.label}
      </span>
    </div>
  );
};

const humanizeStatusCategory = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const lower = trimmed.replace(/_/g, " ").toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

function getGpxPillClassName(gpxStatus: string): string {
  switch (gpxStatus) {
    case "COMPLETE":
      return styles.statusPillSuccess;
    case "IN_PROGRESS":
    case "IN_PROGRESS_RETURNED":
      return styles.statusPillPrimary;
    case "READY_FOR_REVIEW":
      return styles.statusPillWarning;
    default:
      return styles.statusPillNeutral;
  }
}

export const StatusCategoryCellRenderer: React.FC<WfCellParams> = ({ data, context }) => {
  // Child rows: render gpxStatus pill + optional action button
  if (!data?.isParentRow) {
    const gpxStatus = data?.gpxStatus;
    // NOT_STARTED is non-actionable — render nothing (same treatment as BLOCKED in FA status)
    if (!gpxStatus || gpxStatus === "NOT_STARTED") return null;

    // IN_PROGRESS_RETURNED shows the same visual as IN_PROGRESS per product decision,
    // but adds a "View" button so the GP can open the task and see the return note.
    const isReturned = gpxStatus === "IN_PROGRESS_RETURNED";
    const displayStatus = isReturned ? "IN_PROGRESS" : gpxStatus;
    const display = getGpxStatusDisplay(displayStatus);
    const pillClassName = getGpxPillClassName(displayStatus);

    const showActionButton = gpxStatus === "READY_FOR_REVIEW" || isReturned;
    const buttonLabel = gpxStatus === "READY_FOR_REVIEW" ? "Review" : "View";

    const handleActionClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const onTaskClick = context?.onTaskClick;
      // Child rows carry rawTaskId; workflowId lives in path[0] (the parent wf id).
      if (onTaskClick && data?.rawTaskId && data.path[0]) {
        onTaskClick(data.path[0], data.rawTaskId);
      }
    };

    return (
      <div className={styles.cellRow}>
        <span className={`${styles.statusPillBase} ${pillClassName}`}>
          <Icon type={display.icon} color={display.iconColor} size="small" />
          {display.label}
        </span>
        {showActionButton && (
          <button type="button" className={styles.gpxActionButton} onClick={handleActionClick}>
            {buttonLabel}
          </button>
        )}
      </div>
    );
  }

  // Parent rows: render statusCategory as a plain text pill
  const raw = data.statusCategory;
  if (!raw) return null;

  return (
    <div className={styles.cellRow}>
      <span className={`${styles.statusPillBase} ${styles.statusPillNeutral}`}>
        {humanizeStatusCategory(raw)}
      </span>
    </div>
  );
};
